/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import { extend } from 'umi-request';
import { message } from 'antd';
import { Toast } from 'antd-mobile';
import { getLogin, getToken } from '@/utils/bridge';
import DefaultSettings from '../../defaultSettings';
import { md5 } from './md5';
import sentryConfig from '../../config/sentry.config';
import { genRanHex } from '@/utils/utils';

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '服务器找不到请求',
  406: '无法使用请求的内容特性响应请求',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '您的网络发生异常, 连接超时。',
};

const errorHandler = (error: { response: Response }): Response => {
  const { response } = error;
  if (response && response.status) {
    // @ts-ignore
    const errorText = codeMessage[response.status] || response.statusText;
    message.error(errorText);
  }
  return response;
};

/**
 * 配置request请求时的默认参数
 */
const request = extend({
  maxCache: 0,
  credentials: 'include', // 默认请求是否带上cookie
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
  },
  errorHandler,
});

// @ts-ignore
request.interceptors.request.use((url, options) => {
  let Authorization: string | null;

  if (DefaultSettings.projectName === 'qf-react-app-local') {
    Authorization =
      'bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczpcL1wvYXBpLnFpYW5nZmVuLmNvbVwvYXBpXC92MVwvYXV0aFwvbG9naW4iLCJpYXQiOjE2ODU3NzQ4NTcsImV4cCI6MTY4ODM2Njg1NywibmJmIjoxNjg1Nzc0ODU3LCJqdGkiOiIwMDZJRTFiaWF0bmNwNVEwIiwic3ViIjoiZTcwMGI1MzUzYmMxNTZlZDQ2MjA1MDNkYzNjY2UxZGEiLCJwcnYiOiIyM2JkNWM4OTQ5ZjYwMGFkYjM5ZTcwMWM0MDA4NzJkYjdhNTk3NmY3In0.Oc9zuu1r5S6ero2f-8DN1Q1Ul9mRZyu_viCz0bLaMAA';
  } else {
    Authorization = localStorage.getItem('token');
  }

  const timestamp = parseInt(String(new Date().valueOf() / 1000)); // 秒时间戳
  const nonce = genRanHex(16); // 16位随机字符串
  const deviceId = sessionStorage.getItem('deviceId');

  const sign = md5(
    // @ts-ignore
    APP_KEY +
      '&X-Genie-Timestamp=' +
      timestamp +
      '&X-Genie-Nonce=' +
      nonce +
      '&X-Genie-DeviceId=' +
      deviceId,
  );

  return {
    options: {
      ...options,
      headers: {
        Authorization,
        'X-Genie-DeviceId': sessionStorage.getItem('deviceId'), // X-Genie-DeviceId
        'X-Genie-Timestamp': timestamp, // 秒时间戳
        'X-Genie-Nonce': nonce,
        'X-Genie-Sign': sign, // 分配的APPid
        'X-Genie-AppId': '12000', // 分配的APPid
        'X-Genie-Version': sentryConfig.release,
        'X-Genie-Platform': 'web',
      },
    },
  };
});

// @ts-ignore
request.interceptors.response.use(async (response) => {
  try {
    const status = response.status;
    if (status === 200) {
      const data = await response.clone().json();
      if (data.error_code === 1000) {
        getToken();
        throw data.error_msg; // (*)
      }
      if (
        data.error_code === 1002 ||
        data.error_code === 1001 ||
        data.error_code === 1004 ||
        data.error_code === 1005
      ) {
        getLogin();
        throw data.error_msg; // (*)
      }
      if (data.error_code === 2001 || data.error_code > 2001) {
        throw data.error_msg; // (*)
      }
      return response;
    } else {
      throw '您的网络发生异常，无法连接服务器'; // (*)
    }
  } catch (e) {
    Toast.fail(e);
    return response;
  }
});

export default request;
